import { Classes, Dialog, InputGroup, MenuItem } from "@blueprintjs/core"
import { ItemRenderer, MultiSelect } from "@blueprintjs/select"
import { StandardFormButtons } from "modules/common/components/form/StandardFormButtons"
import { filterItemPredicate, matchTargetWidthPopover } from "modules/common/services/form/select"
import { texts } from "modules/common/texts"
import { EmployerDictionaryType } from "modules/spending/employee/models/employee-dictionary"
import React, { useCallback, useMemo, useState } from "react"
import { COLUMN_COLORS, PortfolioSnapshotType } from "../models/board"
import styles from "./PortfolioDialog.module.scss"

const PeopleSelect = MultiSelect.ofType<SelectItem>()

/** Пункт списка с отметкой выбранного — как в окне задания */
const renderMember = (selected: string[]): ItemRenderer<SelectItem> => (item, { handleClick, modifiers }) => {
  if (!modifiers.matchesPredicate) {
    return null
  }

  return (
    <MenuItem
      key={item.id}
      // подсветку пункта под клавиатурой делаем серой: синий фон читался как «выбрано»
      className={modifiers.active ? styles.optionActive : ""}
      disabled={modifiers.disabled}
      icon={selected.includes(item.id) ? "tick" : "blank"}
      onClick={handleClick}
      shouldDismissPopover={false}
      text={item.label}
    />
  )
}

/** Создание и настройка доски заданий: название, цвет, участники */
export const PortfolioDialog: React.FC<PortfolioDialogProps> = ({
  portfolio,
  employee,
  onClose,
  onSubmit,
  onRemove,
}) => {
  const [name, setName] = useState(portfolio?.name ?? "")
  const [color, setColor] = useState(portfolio?.color ?? COLUMN_COLORS[0])
  const [members, setMembers] = useState<string[]>(portfolio?.members.map((m) => m.id) ?? [])
  const [saving, setSaving] = useState(false)

  const selected = members
    .map((id) => employee.employee.find((e) => e.id === id))
    .filter((e) => !!e)
    .map((e) => ({ id: e!.id, label: e!.name }))

  const toggle = useCallback((item: SelectItem) => {
    setMembers((prev) => (prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]))
  }, [])

  // «Отмена» вместо удаления показываем так же, как в окне задания
  const dirty = useMemo(() => {
    const was = { name: portfolio?.name ?? "", color: portfolio?.color ?? COLUMN_COLORS[0] }
    const wasMembers = portfolio?.members.map((m) => m.id) ?? []

    return (
      name !== was.name ||
      color !== was.color ||
      members.length !== wasMembers.length ||
      members.some((id) => !wasMembers.includes(id))
    )
  }, [name, color, members, portfolio])

  const submit = useCallback(
    async () => {
      if (!name.trim() || saving) {
        return
      }

      setSaving(true)
      const success = await onSubmit({
        id: portfolio?.id ?? "",
        name: name.trim(),
        color,
        memberIds: members,
      })
      setSaving(false)

      success && onClose()
    },
    [name, color, members, portfolio, saving, onSubmit, onClose]
  )

  const remove = useCallback(async () => {
    if (!portfolio) {
      return
    }

    setSaving(true)
    const success = await onRemove(portfolio.id)
    setSaving(false)

    success && onClose()
  }, [portfolio, onRemove, onClose])

  return (
    <Dialog
      isCloseButtonShown={true}
      isOpen={true}
      onClose={onClose}
      title={portfolio ? "Доска заданий" : "Новая доска заданий"}
      backdropClassName="standard"
      className={`figma-dialog ${styles.dialog}`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
      <div className={styles.body}>
        <label className="bp5-label">
          Название
          <InputGroup
            className="planr-default-input"
            value={name}
            autoFocus={true}
            placeholder="Например, «Личные задачи»"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </label>

        <div className={styles.colorsBlock}>
          <label className="bp5-label">Цвет</label>
          <div className={styles.colors}>
            {COLUMN_COLORS.map((item) => (
              <button
                type="button"
                key={item}
                className={`${styles.color} ${color === item ? styles.selected : ""}`}
                style={{ backgroundColor: item }}
                onClick={() => setColor(item)}
              />
            ))}
          </div>
        </div>

        <label className="bp5-label">
          Участники
          <PeopleSelect
            className="full-width-select"
            itemRenderer={renderMember(members)}
            itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
            selectedItems={selected}
            items={employee.employee}
            onItemSelect={toggle}
            placeholder="Кого пригласить"
            itemPredicate={filterItemPredicate}
            tagRenderer={(item: SelectItem) => item.label}
            noResults={<MenuItem disabled={true} text={texts.noData} />}
            popoverProps={matchTargetWidthPopover()}
            // после выбора строку поиска сбрасываем: следующего ищут с чистого листа
            resetOnSelect={true}
            tagInputProps={{
              onRemove: (_value: any, index: number) =>
                setMembers((prev) => prev.filter((_, i) => i !== index)),
            }}
          />
        </label>

        <div className={styles.hint}>
          Задания доски не относятся к проектам и видны только вам и приглашённым участникам.
        </div>
      </div>

      {/* кнопки те же, что в окне задания: при изменениях удаление меняется на «Отмена» */}
      <div className={Classes.DIALOG_FOOTER}>
        <StandardFormButtons
          {...({ dirty, isSubmitting: saving, resetForm: onClose, submitForm: submit } as any)}
          isRemoved={false}
          isNewlyCreated={!portfolio}
          what={portfolio ? `доску «${portfolio.name}» вместе со всеми её заданиями` : "доску"}
          onRemove={remove}
          submitLabel={texts.save}
        />
      </div>
      </form>
    </Dialog>
  )
}

interface PortfolioDialogProps {
  portfolio: PortfolioSnapshotType | null
  employee: EmployerDictionaryType
  onClose: () => void
  onSubmit: (value: { id: string; name: string; color: string; memberIds: string[] }) => Promise<boolean>
  onRemove: (id: string) => Promise<boolean>
}
